import type { Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { formatTime } from '@/lib/time'
import { KEY_RE, type FileSpec, type FileStatus } from './schema'

export type FileResource = Resource<FileSpec, FileStatus>

export const defaults = (spec: Partial<FileSpec>): FileSpec => ({ name: spec.name ?? '', ...spec })

/** Mirrors file.CheckName: not blank, no slash, no control characters, not "." or "..". */
export const nameProblem = (name: string): string | null => {
  const t = name.trim()
  if (!t) return 'a name is needed'
  if (t !== name) return 'a name does not start or end with a space'
  if (t === '.' || t === '..') return 'that name is taken by the system'
  if (/[/\\]/.test(t)) return 'a name has no slashes'
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f]/.test(t)) return 'a name has no control characters'
  return null
}

/** Mirrors file.Kind.Validate, less what needs the store (the parent's existence and suite). */
export const validate = (r: FileResource): string[] => {
  const errs: string[] = []
  const why = nameProblem(r.spec.name)
  if (why) errs.push(why)
  const s = r.spec
  if (s.folder) {
    if (s.key || s.thumb_key || s.size || s.sha256 || s.content_type) errs.push('a folder holds no bytes')
  } else if (!s.key || !KEY_RE.test(s.key)) errs.push('a file needs its object key')
  if (s.parent && s.parent === r.id) errs.push('a folder cannot hold itself')
  return errs
}

export const initialStatus = (r: FileResource): FileStatus => (r.spec.folder ? {} : { state: 'uploading' })

export const displayName = (r: FileResource): string => r.spec.name

/** Mirrors internal/kinds/file Apply. Fixtures: testdata/actions/file_*.json. */
export const apply = (r: FileResource, action: string, _args: unknown, at: string): FileResource => {
  const st: FileStatus = { ...r.status }
  if (action === 'complete') {
    if (r.spec.folder) throw invalid('a folder holds no bytes')
    if (st.state === 'ready') throw conflict('already complete')
    st.state = 'ready'
    st.uploaded_at = formatTime(new Date(at))
  } else throw invalid(`unknown action ${action}`)
  return { ...r, status: compact(st) }
}
